import { useState } from 'react';
import { TableColumn, PaginationConfig } from '../../../types/mtls';
import { LoadingSpinner } from './LoadingSpinner';

interface BulkSelectionConfig<T> {
  enabled: boolean;
  selectedItems: T[];
  onToggleItem: (item: T) => void;
  onToggleAll: () => void;
  isSelected: (item: T) => boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  getItemId: (item: T) => string | number;
}

interface MtlsTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  pagination?: PaginationConfig;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onFilter?: (filters: Record<string, any>) => void;
  emptyMessage?: string;
  className?: string;
  bulkSelection?: BulkSelectionConfig<T>;
}

export function MtlsTable<T>({
  data,
  columns,
  loading = false,
  pagination,
  onSort,
  onFilter,
  emptyMessage = 'No data available',
  className = '',
  bulkSelection,
}: MtlsTableProps<T>): JSX.Element {
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');


  const handleSort = (columnKey: string) => {
    if (!onSort) return;

    let newDirection: 'asc' | 'desc' = 'asc';
    if (sortColumn === columnKey && sortDirection === 'asc') {
      newDirection = 'desc';
    }

    setSortColumn(columnKey);
    setSortDirection(newDirection);
    onSort(columnKey, newDirection);
  };



  const getSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return <i className="fas fa-sort text-muted ml-1"></i>;
    }
    return sortDirection === 'asc' 
      ? <i className="fas fa-sort-up text-primary ml-1"></i>
      : <i className="fas fa-sort-down text-primary ml-1"></i>;
  };

  const renderPagination = () => {
    if (!pagination) return null;

    const { page, size, total, onPageChange } = pagination;
    const totalPages = Math.ceil(total / size);
    const startItem = (page - 1) * size + 1;
    const endItem = Math.min(page * size, total);

    return (
      <div className="row mt-3">
        <div className="col-sm-12 col-md-5">
          <div className="dataTables_info">
            Showing {startItem} to {endItem} of {total} entries
          </div>
        </div>
        <div className="col-sm-12 col-md-7">
          <div className="dataTables_paginate paging_simple_numbers">
            <ul className="pagination justify-content-end">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </button>
              </li>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => onPageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}

              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`mtls-table-container ${className}`}>
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="thead-light">
            <tr>
              {bulkSelection?.enabled && (
                <th style={{ width: '40px' }}>
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="select-all"
                      checked={bulkSelection.isAllSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = bulkSelection.isPartiallySelected;
                        }
                      }}
                      onChange={bulkSelection.onToggleAll}
                    />
                    <label className="custom-control-label" htmlFor="select-all"></label>
                  </div>
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`
                    ${column.responsive ? 'd-none d-md-table-cell' : ''}
                    ${column.sortable ? 'cursor-pointer user-select-none' : ''}
                  `}
                  onClick={() => column.sortable && handleSort(String(column.key))}
                >
                  <div className="d-flex align-items-center">
                    {column.title}
                    {column.sortable && getSortIcon(String(column.key))}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={(bulkSelection?.enabled ? 1 : 0) + columns.length} className="text-center py-5">
                  <LoadingSpinner size="lg" text="Loading data..." />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={(bulkSelection?.enabled ? 1 : 0) + columns.length} className="text-center py-5 text-muted">
                  <i className="fas fa-inbox fa-2x mb-3 d-block"></i>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const itemId = bulkSelection?.getItemId(item);
                return (
                  <tr key={itemId || index} className={bulkSelection?.isSelected(item) ? 'table-active' : ''}>
                    {bulkSelection?.enabled && (
                      <td>
                        <div className="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`select-${itemId}`}
                            checked={bulkSelection.isSelected(item)}
                            onChange={() => bulkSelection.onToggleItem(item)}
                          />
                          <label className="custom-control-label" htmlFor={`select-${itemId}`}></label>
                        </div>
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={String(column.key)}
                        className={column.responsive ? 'd-none d-md-table-cell' : ''}
                      >
                        {column.render 
                          ? column.render(item) 
                          : String((item as any)[column.key] || '')
                        }
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
}